/**
 * Línea de tiempo GSAP para orquestar las transiciones del cielo y la
 * aparición de escenas. Complementa las transiciones CSS y de Svelte.
 *
 * Ver docs/SPEC.md §6.1.
 */

import { gsap } from 'gsap';
import { DURACIONES, EASINGS } from './constantes';
import type { PasoId } from '@/types';

export type TransicionFn = (timeline: gsap.core.Timeline) => void;

const MAPA_GRADIENTES: Record<string, string> = {
  dia: 'linear-gradient(180deg, #6b7fa3 0%, #6b7fa3 100%)',
  crepusculo: 'linear-gradient(180deg, #4a3b6a 0%, #1a2238 100%)',
  amanecer: 'linear-gradient(180deg, #d9a85c 0%, #4a3b6a 100%)',
  papel: 'linear-gradient(180deg, #f4ead5 0%, #f4ead5 100%)',
  noche: 'linear-gradient(180deg, #1a2238 0%, #1a2238 100%)',
};

/** Mapa de pasos a su gradiente de cielo. */
export const GRADIENTE_POR_PASO: Record<PasoId, string> = {
  INICIO: MAPA_GRADIENTES.papel ?? '',
  SOL: MAPA_GRADIENTES.crepusculo ?? '',
  MUSICA: MAPA_GRADIENTES.crepusculo ?? '',
  LUCIERNAGAS: MAPA_GRADIENTES.crepusculo ?? '',
  GIRASOLES: MAPA_GRADIENTES.crepusculo ?? '',
  AMANECER: MAPA_GRADIENTES.amanecer ?? '',
  CARTA: MAPA_GRADIENTES.papel ?? '',
};

/** Transiciones GSAP por paso. */
export const TRANSICIONES: Record<PasoId, TransicionFn> = {
  INICIO: () => {
    // Sin transición inicial
  },

  SOL: (tl) => {
    tl.to('.cielo', {
      backgroundImage: GRADIENTE_POR_PASO.SOL,
      duration: DURACIONES.sol / 1000,
      ease: EASINGS.suave,
    });
    tl.from('.luna', { opacity: 0, scale: 0.5, duration: 2, ease: EASINGS.rebote }, 0.5);
  },

  MUSICA: () => {
    // Solo el audio arranca, no hay cambio visual
  },

  LUCIERNAGAS: (tl) => {
    tl.to('.luciernagas-fallback, .canvas-luciernagas', {
      opacity: 1,
      duration: DURACIONES.luciernagas / 1000,
      ease: EASINGS.entrada,
    });
  },

  GIRASOLES: (_tl) => {
    // La animación orquestada y orgánica de florecimiento del jardín
    // se gestiona con transiciones CSS cubic-bezier y delays escalonados por capas en Girasoles.svelte
  },

  AMANECER: (_tl) => {
    // La transición atmosférica a la mañana (luz cálida, fondo Ghibli mimetizado,
    // polen dorado y fade-out de estrellas) se orquesta armónicamente con CSS en Cielo.svelte
  },

  CARTA: (tl) => {
    tl.to('.cielo, .girasoles-contenedor, .polen-contenedor, .canvas-luciernagas', {
      opacity: 0,
      duration: DURACIONES.carta / 1000,
      ease: EASINGS.suave,
    });
  },
};

/**
 * Ejecuta la transición de un paso a otro.
 */
export function ejecutarTransicion(desde: PasoId, hasta: PasoId, alCompletar: () => void): void {
  if (desde === hasta) {
    alCompletar();
    return;
  }

  const tl = gsap.timeline({ onComplete: alCompletar });
  const transicion = TRANSICIONES[hasta];
  if (transicion) {
    transicion(tl);
  }
}

/**
 * Reinicia la experiencia con fade-out del body.
 */
export function reiniciarExperiencia(): void {
  const tl = gsap.timeline({
    onComplete: () => window.location.reload(),
  });
  tl.to('body', { opacity: 0, duration: DURACIONES.reset / 1000 });
}
